import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-[#1E2328]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-[#3aa0e0] hover:text-[#3aa0e0] mb-8 transition-colors">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold text-white mb-8">Cookie Policy</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-400 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. What Are Cookies?</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Cookies are small text files that are placed on your device when you visit a website or use an application. They are widely used to make applications work more efficiently and provide information to the owners of the application.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              While FlyFinder is primarily a mobile application, we may use similar technologies (such as local storage, device identifiers, and analytics SDKs) to provide and improve our services. For simplicity, we refer to all these technologies as "cookies" in this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Cookies</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              FlyFinder uses cookies and similar technologies for the following purposes:
            </p>
            
            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.1 Essential Cookies</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              These are necessary for the App to function properly and cannot be disabled. They include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Authentication:</strong> To keep you logged into your account</li>
              <li><strong>Security:</strong> To detect and prevent fraudulent activity</li>
              <li><strong>Session Management:</strong> To remember your preferences during a session</li>
              <li><strong>Load Balancing:</strong> To distribute requests across our servers</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.2 Functional Cookies</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              These enhance the functionality and personalization of the App:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Preferences:</strong> To remember your settings and preferences (e.g., units of measurement, notification settings)</li>
              <li><strong>Location Data:</strong> To store your recent location searches for faster recommendations</li>
              <li><strong>Offline Access:</strong> To enable offline functionality for Pro users</li>
              <li><strong>Personalization:</strong> To customize content based on your fishing history and preferences</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.3 Analytics Cookies</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              These help us understand how users interact with the App:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Usage Statistics:</strong> To track which features are most popular</li>
              <li><strong>Performance Monitoring:</strong> To identify and fix technical issues</li>
              <li><strong>User Behavior:</strong> To understand navigation patterns and improve user experience</li>
              <li><strong>A/B Testing:</strong> To test new features and improvements</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.4 Marketing Cookies</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              These are used to deliver relevant advertisements and track their effectiveness:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Targeted Advertising:</strong> To show you relevant ads based on your interests (Free plan users)</li>
              <li><strong>Campaign Tracking:</strong> To measure the effectiveness of our marketing campaigns</li>
              <li><strong>Social Media Integration:</strong> To enable sharing features and track social media interactions</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              <em>Note: Pro subscribers enjoy an ad-free experience and are not shown marketing content.</em>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Third-Party Cookies</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We work with third-party service providers who may set cookies on your device when you use the App. These include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Analytics Providers:</strong> Google Analytics, Firebase Analytics</li>
              <li><strong>Payment Processors:</strong> Apple Pay, Google Pay (for in-app purchases)</li>
              <li><strong>Cloud Services:</strong> For data storage and hosting</li>
              <li><strong>Advertising Networks:</strong> For serving ads to Free plan users</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              These third parties have their own privacy policies. We do not control how they use cookies or collect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Cookie Duration</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Cookies can be either "session" cookies or "persistent" cookies:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Session Cookies:</strong> Temporary cookies that are deleted when you close the App</li>
              <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until you delete them. We use persistent cookies to remember your login status and preferences.</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              Most of our cookies are set to expire within 30 days to 1 year, depending on their purpose.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Managing Cookies</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              You have several options for managing cookies:
            </p>
            
            <h3 className="text-xl font-bold text-white mb-3 mt-6">5.1 App Settings</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              You can manage certain cookie preferences directly within the FlyFinder App:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Go to Settings > Privacy & Data</li>
              <li>Toggle analytics and marketing preferences on or off</li>
              <li>Note: Essential cookies cannot be disabled as they are necessary for the App to function</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">5.2 Device Settings</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              You can also control cookies through your device settings:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>iOS:</strong> Settings > Privacy > Tracking > Disable "Allow Apps to Request to Track"</li>
              <li><strong>Android:</strong> Settings > Google > Ads > Opt out of Ads Personalization</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">5.3 Advertising Choices</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              To opt out of interest-based advertising:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Visit the Digital Advertising Alliance: <a href="http://www.aboutads.info/choices/" className="text-[#3aa0e0] hover:text-[#3aa0e0]" target="_blank" rel="noopener noreferrer">www.aboutads.info/choices</a></li>
              <li>Visit the Network Advertising Initiative: <a href="http://www.networkadvertising.org/choices/" className="text-[#3aa0e0] hover:text-[#3aa0e0]" target="_blank" rel="noopener noreferrer">www.networkadvertising.org/choices</a></li>
              <li>Upgrade to FlyFinder Pro for a completely ad-free experience</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">5.4 Clear App Data</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              You can clear all cookies and app data by:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>iOS:</strong> Settings > General > iPhone Storage > FlyFinder > Delete App</li>
              <li><strong>Android:</strong> Settings > Apps > FlyFinder > Storage > Clear Data</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              <em>Note: Clearing app data will log you out and reset your preferences.</em>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Impact of Disabling Cookies</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you disable or refuse cookies, some parts of the App may not function properly. For example:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>You may need to log in every time you open the App</li>
              <li>Your preferences and settings may not be saved</li>
              <li>Personalized recommendations may be less accurate</li>
              <li>Some features may be unavailable or limited</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Do Not Track Signals</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Some browsers and devices have a "Do Not Track" (DNT) feature. Currently, there is no industry standard for how to respond to DNT signals. We do not currently respond to DNT signals from browsers or mobile devices.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Updates to This Cookie Policy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. More Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              For more information about our privacy practices, please see our <Link to="/privacy" className="text-[#3aa0e0] hover:text-[#3aa0e0]">Privacy Policy</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. Contact Us</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have questions or concerns about our use of cookies, please contact us at:
            </p>
            <p className="text-slate-300 leading-relaxed">
              <strong>Email:</strong> <a href="mailto:info@flyfinder.ca" className="text-[#3aa0e0] hover:text-[#3aa0e0]">info@flyfinder.ca</a><br />
              <strong>Subject Line:</strong> Cookie Policy Inquiry
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
